<template>
  <Container>
    <Card>
      <div :class="$style.header">
        <div :class="$style.titleBlock">
          <HeadingSmall>
            Fleet distribution
          </HeadingSmall>
          <div :class="$style.titleTab">
            » {{ currentTabName }}
          </div>
        </div>
        <div :class="$style.filterBlock">
          <ParagraphBody
            :class="$style.text"
            weight="semibold"
          >
            Time frame:
          </ParagraphBody>
          <SystemFleetDistributionDatePicker
            :defaultTimeFrame="defaultTimeFrame"
            @change="handleLastContactFilter"
          />
          <ButtonSecondary
            :class="$style.filterButton"
            :text="filterButtonText"
            @click="filterBtnAction"
          />
        </div>
      </div>
      <div :class="$style.distribution">
        <div
          v-if="showDonutChart"
          :class="$style.donutChart"
        >
          <OsManagementDonutChart
            :activeTabIndex="activeTabIndex"
            :chartData="donutChartData"
            :halfSizeChart="true"
            :showLegend="true"
            @selectDataPoint="handleChartClick"
          />
        </div>
        <div
          v-else
          :class="$style.donutChart"
          data-test-id="SystemFleetDistributionCard__zeroStateImage"
        >
          <img
            :class="$style.graphic"
            :src="`${S3BucketAddress}/images/graphic-mdm-overlay.png`"
          >
          <ParagraphBody>0 Devices have been active in 30 days</ParagraphBody>
        </div>
        <div
          v-if="tabs.length"
          :class="$style.distributionTable"
        >
          <ChartSummaryCards
            :cardsData="chartData"
            :daysFilter="lastContactFilterDefaultValue"
            :visibleCard="activeTabIndex"
          />
        </div>
      </div>
    </Card>
  </Container>
</template>

<script>
import { cloneDeep } from 'lodash';

import { mapActions, mapState } from 'vuex';

import {
  ButtonSecondary,
  Container,
  HeadingSmall,
  ParagraphBody,
} from '@jumpcloud/ui-components';
import { ItemTypeConsts, StoreModuleMap } from 'client/util/Constants';
import { S3BucketAddress } from 'client/util/Constants/ApplicationConstants';
import { eventFields } from 'client/Insights/ActivityLog/constants/EventMetadata';
import {
  quickPicksMetadata,
  quickPicksOptions,
} from 'client/Insights/ActivityLog/constants/QuickPicksConsts';
import Card from 'components/Card.vue';
import ChartSummaryCards from 'client/OsManagement/DonutChart/ChartSummaryCards.vue';
import EventTypes from 'client/Insights/ActivityLog/constants/EventTypes';
import OsManagementDonutChart from 'client/OsManagement/DonutChart/OsManagementDonutChart.vue';
import SystemFilters
  from 'client/components/ItemTable/CollectionFilterComponents/constants/SystemFilters';
import SystemFleetDistributionDatePicker from 'client/systems/SystemFleetDistributionDatePicker';

const {
  osMinorRollback, osMinorUpgrade, osMajorRollback, osMajorUpgrade, systemCreate, systemDelete,
} = EventTypes;
const insightsEventModule = StoreModuleMap[ItemTypeConsts.insightsEvent];

export default {
  name: 'SystemFleetDistributionCard',

  components: {
    ButtonSecondary,
    Card,
    ChartSummaryCards,
    Container,
    HeadingSmall,
    OsManagementDonutChart,
    SystemFleetDistributionDatePicker,
    ParagraphBody,
  },

  props: {
    activeTabIndex: {
      type: Number,
      default: 0,
    },
    activeTabName: {
      type: String,
      default: 'activity',
    },
    lastContactSum: {
      type: Array,
      default: () => [],
    },
    osListFormatted: {
      type: Array,
      default: () => [],
    },
    supportedOSes: {
      type: Array,
      default: () => [],
    },
    tabs: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      quickPicksMetadata,
      S3BucketAddress,
      lastContactFilterDefaultValue: 7,
      lastContactFilterValues: [
        {
          label: '7 Days',
          value: 7,
        },
        {
          label: '30 Days',
          value: 30,
        },
      ],
      allEventTypes: [
        systemDelete,
        systemCreate,
        osMinorUpgrade,
        osMajorUpgrade,
        osMinorRollback,
        osMajorRollback,
      ],
      defaultTimeFrame: quickPicksMetadata[quickPicksOptions.sevenDays],
      disableLastActiveButtons: false,
    };
  },

  computed: {
    ...mapState('OsDistributionModule', ['osList']),

    chartData() {
      return !this.osListEmpty ? this.donutChartData : this.zeroStateOsList;
    },

    currentTabName() {
      return this.tabs[this.activeTabIndex].label;
    },

    dateFilter() {
      let days = 7;
      switch (this.lastContactFilterDefaultValue) {
        case 1:
          days = quickPicksOptions.twentyFourHours;
          break;
        case 3:
          days = quickPicksOptions.threeDays;
          break;
        case 7:
          days = quickPicksOptions.sevenDays;
          break;
        case 15:
          days = quickPicksOptions.fifteenDays;
          break;
        case 30:
          days = quickPicksOptions.thirtyDays;
          break;
        case 45:
          days = quickPicksOptions.fortyFiveDays;
          break;
        case 60:
          days = quickPicksOptions.sixtyDays;
          break;
        case 90:
          days = quickPicksOptions.ninetyDays;
          break;
        default:
          days = quickPicksOptions.sevenDays;
      }
      return days;
    },

    donutChartData() {
      const otherCategory = {
        name: 'Others',
        osVersionList: [],
        sum: 0,
      };

      cloneDeep(this.osList).filter(osObj => !this.supportedOSes.includes(osObj.name))
        .forEach(os => {
          otherCategory.sum += os.sum;
        });

      const donutChartData = otherCategory.sum > 0
        ? [...this.osListFormatted, otherCategory] : this.osListFormatted;

      return donutChartData.map(os => ({
        name: `${os.name} (${os.sum})`,
        osVersionList: os.osVersionList,
        sum: os.sum,
      })).filter(item => item.osVersionList.length > 0);
    },

    filterButtonText() {
      return this.currentTabName === 'Activity' ? 'View Activity Log' : 'View All Devices';
    },

    osListEmpty() {
      return this.osList.length === 0;
    },

    showDonutChart() {
      return !this.osListEmpty && this.donutChartData.length > 0;
    },

    zeroStateOsList() {
      const defaultZeroStateList = [
        {
          name: 'macOS',
          sum: 0,
          osVersionList: [
            {
              name: ';Big Sur;11.6.7;21H15',
              sum: 0,
            },
            {
              name: ';Catalina;10.15.7;19H15',
              sum: 0,
            },
            {
              name: ';Mojave;10.15.7;18H15',
              sum: 0,
            },
          ],
        },
        {
          name: 'Windows',
          sum: 0,
          osVersionList: [
            {
              name: '11.0.17763',
              sum: 0,
            },
          ],
        },
      ];
      const linuxZeroStateList = {
        name: 'Linux',
        sum: 0,
        osVersionList: [
          {
            name: 'Ubuntu;Focal Fossa;20.4.4;LTS',
            sum: 0,
          },
          {
            name: 'CentOS;;6.4.2105;',
            sum: 0,
          },
          {
            name: 'Fedora;;35.0.0;',
            sum: 0,
          },
        ],
      };

      return [...defaultZeroStateList, linuxZeroStateList];
    },
  },

  created() {
    this.filtersSetup();
  },

  destroyed() {
    this.resetState();
  },

  methods: {
    ...mapActions('OsDistributionModule', ['fetchDistributionLastContact', 'resetState']),

    ...mapActions(insightsEventModule, [
      'clearFilters',
      'setFilter',
      'setQuickPickDate',
    ]),

    ...mapActions('SystemCollection', [
      'setFilters',
    ]),

    filterBtnAction() {
      if (this.currentTabName === 'Activity') {
        // Clear any existing Activity Log filters
        this.clearFilters();

        // Set the time range
        this.setQuickPickDate(this.dateFilter);
        // Set the Events filter for the Activity Log
        this.setFilter({
          field: eventFields.eventType,
          value: this.allEventTypes,
        });

        this.$router.push('/insights');
      } else {
        this.setFilters({ filtersArray: [SystemFilters.osName(this.currentTabName)] });
        this.$router.push({ name: 'devicesList' });
      }
    },

    filtersSetup() {
      if (this.lastContactSum[0] === 0) {
        // If there are no devices in the 7 days filter
        // set the last contact filter to 30 days and disable the buttons

        // Change SystemFleetDistributionDatePicker time frame label
        this.$emit('changeTimeFrame', 30);
        this.defaultTimeFrame = quickPicksMetadata[quickPicksOptions.thirtyDays];

        this.lastContactFilterDefaultValue = this.lastContactFilterValues[1].value;
        this.lastContactFilterValues[0].disabled = true;
        this.disableLastActiveButtons = true;
      }
      // Activity tab by default.
      this.$emit('changeTab', 'activity');
    },

    handleChartClick(newActiveTabIndex) {
      if (this.activeTabIndex === 0) {
        const newIndex = parseInt(newActiveTabIndex, 0);
        if (newIndex < this.tabs.length - 1) {
          this.$emit('changeTab', this.tabs[newIndex + 1]);
        }
      }
    },

    async handleLastContactFilter(value) {
      this.$emit('changeTimeFrame', value);
      this.lastContactFilterDefaultValue = value;
      await this.fetchDistributionLastContact(value);
      this.keepOnTheSameTab(value);
    },

    keepOnTheSameTab(days) {
      if (this.activeTabName !== 'activity') {
        if (this.osListEmpty
          || !this.tabs.some((element) => this.activeTabName.includes(element.label))
        ) {
          this.activeTabIndex = 0;
          this.$emit('changeTab', 'activity');
        } else {
          this.$emit('changeTab', this.activeTabName.replace(/[0-9]/g, '') + days);
        }
      }
    },
  },
};
</script>

<style module>
  .header {
    align-items: baseline;
    display: flex;
    justify-content: space-between;
  }

  .graphic {
    margin: 0 9.375rem 3.125rem 0;
    width: 18rem;
  }

  .titleBlock {
    display: flex;
    flex: 0 0 35%;
  }

  .titleTab {
    color: var(--jc-color-neutral-text-subdued);
    font-size: var(--jcBodyLarge);
    line-height: 1.8rem;
    padding-left: 1rem;
    padding-top: 0.063rem;
    text-transform: capitalize;
    word-spacing: 0.3rem;
  }

  .filterBlock {
    align-items: baseline;
    display: flex;
    justify-content: flex-end;
  }

  .filterButton {
    margin-left: 0.5rem;
  }

  .filterBlock .text {
    color: var(--jc-text-color-dark);
    padding-right: 23px;
  }

  .distribution {
    align-items: center;
    display: flex;
    height: 370px;
    justify-content: space-between;
  }

  .distribution .donutChart {
    flex: 0 0 auto;
    padding: 10px 70px 0 55px;
  }

  .distributionTable {
    flex: 1;
    height: 100%;
  }

</style>
